import { Attachment } from "@/types/attachment";
import {
  ensureAttachmentPreview,
  validateAttachments,
} from "@/util/attachmentValidator";
import { getFilePreview } from "@/util/getFilePreview";
import { UploadFile, UploadProps } from "antd";
import { useCallback, useRef, useState } from "react";
import { MAX_UPLOAD_FILE_COUNT } from "../common/const";
import { useToast } from "../component/Toast/useToast";

const DEFAULT_MAX_UPLOAD_SIZE = 100 * 1024 * 1024; // 100MB
const DEFAULT_MAX_FILE_COUNT = MAX_UPLOAD_FILE_COUNT;
const DEFAULT_ACCEPT =
  "image/*,video/*,audio/*,.mp3,audio/mpeg,.wav,audio/wav,.m4a,audio/x-m4a,.aac,audio/aac";

export interface UseFileUploadOptions {
  accept?: string;
  maxSize?: number;
  maxCount?: number;
  onSuccess?: (attachment: Attachment) => void;
  onError?: (error: { tooMany?: boolean; oversized?: boolean }) => void;
  onAttachmentListChange?: (attachments: Attachment[]) => void;
}

/**
 * 通用文件上传 hook
 * 处理文件选择、验证、预览生成等逻辑
 */
export function useFileUpload(options: UseFileUploadOptions = {}) {
  const {
    accept = DEFAULT_ACCEPT,
    maxSize = DEFAULT_MAX_UPLOAD_SIZE,
    maxCount = DEFAULT_MAX_FILE_COUNT,
    onSuccess,
    onError,
  } = options;
  const { error: showErrorToast } = useToast({
    key: "file-upload-toast",
  });

  const [attachmentListState, setAttachmentListState] = useState<Attachment[]>(
    []
  );
  // 追踪最新列表，避免 React state 更新的异步批处理导致的读取延迟
  const attachmentListRef = useRef<Attachment[]>([]);
  // 用于追踪正在处理的文件，避免并发问题
  const processingQueueRef = useRef<Promise<void>>(Promise.resolve());

  const setAttachmentList = useCallback(
    (updater: Attachment[] | ((prev: Attachment[]) => Attachment[])) => {
      const nextList =
        typeof updater === "function"
          ? (updater as (prev: Attachment[]) => Attachment[])(
              attachmentListRef.current
            )
          : updater;

      attachmentListRef.current = nextList;
      setAttachmentListState(nextList);
      options.onAttachmentListChange?.(nextList);
    },
    [options.onAttachmentListChange]
  );

  const uploadProps: UploadProps = {
    fileList: [],
    onChange: async ({ file: newFile }) => {
      // 将新文件的处理加入队列，确保顺序执行
      processingQueueRef.current = processingQueueRef.current.then(async () => {
        const rawFile =
          (newFile as UploadFile).originFileObj ?? (newFile as unknown as File);

        const newAttachment: Attachment = {
          type: "file",
          preview: "",
          file: rawFile,
          id: newFile.uid,
        };

        const { valid, errors } = validateAttachments(
          [...attachmentListRef.current, newAttachment],
          {
            maxTotalSize: maxSize,
            maxFileCount: maxCount,
          }
        );

        const shouldAccept = valid.some((a) => a.id === newAttachment.id);

        // 处理验证错误通知
        if (errors.tooMany || errors.oversized) {
          if (onError) {
            onError(errors);
          } else {
            if (errors.tooMany) {
              showErrorToast(
                `You can only upload up to ${maxCount} files at a time.`
              );
            } else if (errors.oversized) {
              showErrorToast(
                `The file size exceeds the maximum limit of ${
                  maxSize / 1024 / 1024
                }MB.`
              );
            }
          }
          return; // 早返回，不继续处理被拒绝的文件
        }

        // 只为被接受的附件生成预览
        if (shouldAccept) {
          setAttachmentList(valid);

          try {
            const withPreview = await ensureAttachmentPreview(
              newAttachment,
              getFilePreview
            );

            // 使用函数式更新，确保不会丢失其他文件
            setAttachmentList((current) => {
              // 检查文件是否还在列表中(可能被用户删除了)
              const exists = current.some(
                (item) => item.id === newAttachment.id
              );
              if (!exists) {
                return current;
              }

              // 更新预览，确保保留所有其他文件
              return current.map((item) =>
                item.id === newAttachment.id ? withPreview : item
              );
            });

            // 预览生成成功后调用 onSuccess
            onSuccess?.(withPreview);
          } catch (error) {
            console.error("Failed to generate preview:", error);
            // 预览生成失败不影响附件本身，继续保留附件
            // 即使预览生成失败，附件已被接受，仍然调用 onSuccess
            onSuccess?.(newAttachment);
          }
        }
      });
    },
    beforeUpload: () => {
      return false; // 阻止自动上传
    },
    multiple: true,
    showUploadList: false,
    accept,
  };

  const onRemove = useCallback((attachment: Attachment) => {
    setAttachmentList((prev) =>
      prev.filter((item) => item.id !== attachment.id)
    );
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachmentList([]);
  }, []);

  return {
    attachmentList: attachmentListState,
    setAttachmentList,
    uploadProps,
    onRemove,
    clearAttachments,
  };
}
